import React, { useEffect, useContext, useRef, useState } from 'react'

// Node Modules
import parse from 'html-react-parser'

//Components
import Loading from 'components/Loading'

// Context
import { HashContext } from 'context/page-context'
import { CasLanguageState} from 'context/cas-format-context'
import {LoadingContext} from 'context/loading-context'
import {CurrentContentContext} from 'context/content-context'
import { DocsetLatestContext } from 'context/docset-latest-context'

// Styles
import styles from './Document.module.css'

//Types
import {Content} from 'types/docset'

// Util
import xisParser from 'util/xis-parser'
import { HELP_DOCSET_PAGE_NOT_FOUND, LANGUAGE_ENGLISH, DOCSET_LATEST_CFR  } from 'util/constants'

export interface Props {
  alias: string
  cfr: string
  page: string
  content: Content | undefined,
  help: boolean | undefined,
  lang: string | undefined,
  isViya4: boolean
}

const Document = ({ alias, cfr, page, content, help, lang, isViya4 }: Props) => { 
  const [loading, setLoading] = useState(true)
  const options = xisParser({ alias, cfr, page, help})
  const elements = parse(content ? content.content : '', options)


  const fragment = useContext(HashContext)
  const documentRef = useRef<HTMLElement>(null)
  const casLang = useContext(CasLanguageState)
  const loadingContext = useContext(LoadingContext)
  const currentContentContext = useContext(CurrentContentContext)
  const docsetLatestContext = useContext(DocsetLatestContext)

  useEffect(() => {
    if (!documentRef || !documentRef.current) {
      return
    }

    const Prism = (window as any).Prism
    
    if (typeof Prism != "undefined") {
      var itemsNeedHighlighted = documentRef.current.querySelectorAll('code[class^="language"]');
      
      itemsNeedHighlighted.forEach(item => {
        Prism.highlightElement(item)
      })
    }
  }, [casLang, loading])

  useEffect(() => {
    const scrollTarget = fragment && fragment.replace("#", "")
    const el = scrollTarget ? document.getElementById(scrollTarget) : document.getElementById('MainContentSection')
    if (scrollTarget ) {  // if we have an anchor
     
      setTimeout(() =>{el && el.scrollIntoView()}, 100)
      
      if (!el?.hasAttribute('tabindex')) {
        el?.setAttribute('tabIndex', '-1')
      }
      el?.focus()

    } else if(el) {  //if no anchor, check for maincontentsection element and then scroll it to top
      el.scrollTop = 0;
    }
    
  }, [loading, fragment])

  useEffect(()=> {
    let currContent = currentContentContext.currentContent
    if (!loadingContext.loading && currContent.alias === content?.alias && currContent.cfr === content.cfr && currContent.page === content.page) {
      setTimeout(()=>setLoading(false), 500)
    } else {
      content?.page ===HELP_DOCSET_PAGE_NOT_FOUND ? setLoading(false) : setLoading(true)
    }
  }, [content, loadingContext.loading])/* eslint-disable-line */

  useEffect(()=>{
    if (!loading) {

      //handle stylesheets
      let hcStyles = document.getElementsByName('data-xis-css-link')
      let activeStyles = document.querySelectorAll('[data-xis-css-ref]')

      hcStyles.forEach(style => {
        let found = false;
        activeStyles.forEach(active => {
          let activeName = active.getAttribute('data-xis-css-ref')
          if (activeName === style.id) found = true
        })
        if (!found) {
          document.head.removeChild(style)
        }
      })

      //handle canonical links
      let noIndexFound = document.head.querySelectorAll('[content="noindex"]');
      let bodyContentNoneFound = document.body.querySelectorAll('[content="none"]')
      let bodyNoIndexFound = document.body.querySelectorAll('[content="noindex"]')
      let prevCanonical = document.head.querySelectorAll('[rel="canonical"]');
      let currContent = currentContentContext.currentContent

      if ( !help && (bodyNoIndexFound.length === 0 && bodyContentNoneFound.length === 0) && ( !isViya4 || !docsetLatestContext || docsetLatestContext === currContent.cfr)) { //see if canonical should be made or not
        let canonicalUrl = `https://documentation.sas.com/doc/${lang || LANGUAGE_ENGLISH }/${currContent.alias}/${ !isViya4 || !docsetLatestContext ? currContent.cfr : DOCSET_LATEST_CFR }/${currContent.page}`
        //create canonical
        if (prevCanonical && prevCanonical.length !== 0) { //replace href of previous canonical
          let prev = prevCanonical[0]
          if (prev) prev.setAttribute('href', canonicalUrl)
        } else {  //create new canonical link in head
          let link = document.createElement("link")
          link.rel = "canonical"
          link.href = canonicalUrl
          document.head.appendChild(link);
        }
        
        //remove noindex found if there is one in the head
        if (noIndexFound && noIndexFound.length !==0) {
          document.head.removeChild(noIndexFound[0])
        } 
      } 
      else {  //page should not be indexed
        if (noIndexFound.length === 0) {  //if there isn't already a noindex tag
          let noIndex = document.createElement('meta')
          noIndex.name='robots'
          noIndex.content='noindex'
          document.head.appendChild(noIndex);
        }

        //remove any previous canonicals
        if (prevCanonical && prevCanonical.length !== 0) {
          let prev = prevCanonical[0]
          document.head.removeChild(prev)
        }
      }

    }
  },[loading])/* eslint-disable-line */

  return (
    <article id="document-content" ref={documentRef} className={styles.document}>
      { !loading ? elements :  <Loading />}
    </article>
  )
}

export default Document